import React, { useState, useEffect } from 'react';
import { newsService } from '../../../services/newsService';
import { NewsItem, DashboardStats } from '../../../types';

const Dashboard: React.FC = () => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const [newsData, statsData] = await Promise.all([
        newsService.getAllNews(),
        newsService.getDashboardStats(),
      ]);
      setNewsItems(newsData);
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (newsId: string) => {
    try {
      await newsService.markAsRead(newsId);
      setNewsItems(prev =>
        prev.map(item =>
          item.id === newsId ? { ...item, isRead: true } : item
        )
      );
    } catch (error) {
      console.error('Erro ao marcar notícia como lida:', error);
    }
  };

  const getImpactIcon = (impact: string) => {
    switch (impact) {
      case 'positive': return '📈';
      case 'negative': return '📉';
      default: return '➡️';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'positive': return 'var(--success-green)';
      case 'negative': return 'var(--danger-red)';
      default: return 'var(--neutral-gray)';
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen-1/2 gap-4">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
        <p>Carregando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-lg text-gray-600">Hoje, {stats?.totalNews || 0} notícias impactam suas ações.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 flex items-center gap-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="text-3xl w-16 h-16 rounded-full flex items-center justify-center bg-green-100">📈</div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Positivas</h3>
            <span className="text-3xl font-bold text-gray-900">{stats?.positiveNews || 0}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 flex items-center gap-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="text-3xl w-16 h-16 rounded-full flex items-center justify-center bg-red-100">📉</div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Negativas</h3>
            <span className="text-3xl font-bold text-gray-900">{stats?.negativeNews || 0}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 flex items-center gap-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="text-3xl w-16 h-16 rounded-full flex items-center justify-center bg-gray-100">➡️</div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Neutras</h3>
            <span className="text-3xl font-bold text-gray-900">{stats?.neutralNews || 0}</span>
          </div>
        </div>
      </div>

      <div className="news-list">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Notícias do dia</h2>
        {newsItems.map(item => (
          <div
            key={item.id}
            className={`bg-white rounded-xl p-6 mb-4 shadow-sm border border-gray-200 cursor-pointer transition-all duration-200 flex gap-4
              ${item.isRead ? 'opacity-70' : 'border-l-4 border-blue-700'}`}
            onClick={() => markAsRead(item.id)}
          >
            <div className="flex-shrink-0">
              <span
                className="text-2xl w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"
                style={{ color: getImpactColor(item.impact) }}
              >
                {getImpactIcon(item.impact)}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-gray-900 leading-tight flex-1 mr-4">{item.title}</h3>
                <span className="text-sm text-gray-600 whitespace-nowrap">{item.time}</span>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">{item.summary}</p>
              <div className="flex gap-4 items-center">
                <span className="text-blue-700 font-medium text-sm">{item.company}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase
                  ${item.impact === 'positive' ? 'bg-green-100 text-green-500' :
                  item.impact === 'negative' ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-600'}`}>
                  {item.impact === 'positive'
                    ? 'Positivo'
                    : item.impact === 'negative'
                    ? 'Negativo'
                    : 'Neutro'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;




